// D119.13 - Accounts "Margin by vessel" and an invoice's pinned TT$ rate. Typed over lib/apiClient.
import { get } from './apiClient';

/** One delivery (a client invoice), TT$ at the invoice's own pinned rate. null = not stated (never 0). */
export type MarginInvoiceRow = {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  status: string;
  currency: string | null;
  /** TT$ per US$1 the invoice is pinned at; null for a TT$ invoice or a US$ one with no rate. */
  usdToTtd: number | null;
  /** No line total at all (not a total of 0): there is no Sell to show, and no rate is missing for it. */
  nothingBilled: boolean;
  /** Everything billed, net of credit notes (lines with no cost included). */
  sellTtd: number | null;
  costTtd: number | null;
  /** Margin of the lines that have a cost only. */
  marginTtd: number | null;
  /** Sale of the lines that have a cost: what marginPct is over. */
  costedSellTtd: number | null;
  marginPct: number | null;
  hasCostData: boolean;
  /** Lines that still sell for money (net of credits) but carry no cost: left out of the margin. */
  uncostedLines: number;
};

export type MarginFigures = {
  deliveries: number;
  /** null when no delivery in the group has a TT$ Sell (no pinned rate, or nothing billed) - never 0. */
  sellTtd: number | null;
  /** Each figure is the sum of its deliveries' own (the drill-down adds up): Cost counts every line that has
   *  one; margin only lines with a sale AND a cost (`incomplete` says the rest are missing); credit notes
   *  are already off the sale. marginPct is over the sale of costed lines. null when none do - never 0. */
  costTtd: number | null;
  marginTtd: number | null;
  marginPct: number | null;
  uncostedLines: number;
  uncostedInvoices: number;
  /** Non-TT$ invoices with no pinned rate to convert with. */
  unconvertedInvoices: number;
  /** Invoices with nothing billed (no line total): no Sell, whatever the currency. */
  unbilledInvoices: number;
  incomplete: boolean;
};

export type MarginVessel = MarginFigures & { vessel: string; invoices: MarginInvoiceRow[] };

export type MarginByVesselReport = { from: string; to: string; vessels: MarginVessel[]; totals: MarginFigures };

export async function fetchMarginByVessel(range: { from?: string; to?: string } = {}) {
  const q = new URLSearchParams();
  if (range.from) q.set('from', range.from);
  if (range.to) q.set('to', range.to);
  const query = q.toString();
  const res = await get<{ success: boolean; data: MarginByVesselReport }>(`/client-invoices/margin-by-vessel${query ? `?${query}` : ''}`);
  return res.data;
}

/** TT$ per US$1 the invoice is pinned at; null = none pinned (no TT$ figures are shown). */
export async function fetchInvoicePinnedRate(id: string) {
  const res = await get<{ success: boolean; data: { currency: string | null; usdToTtd: number | null } }>(
    `/client-invoices/${encodeURIComponent(id)}/pinned-rate`
  );
  return res.data;
}
